import React from 'react'
import styled from 'styled-components'
import { smsCount } from './plainTemplate'

// What the two message editors of the reschedule settings share: the boxes,
// the token buttons, the preview bubble and the switch to the code editor.

export const MUTED = '#6b7280'
export const ERROR_COLOR = '#dc2626'
const WARNING_COLOR = '#b45309'

export const Note = styled.p`
  margin: 0 0 1rem;
  font-size: 0.8rem;
  color: ${MUTED};
  line-height: 1.5;
`

export const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 0.625rem 0.75rem;
  border: 1px solid ${({ $invalid }) => ($invalid ? ERROR_COLOR : '#d1d5db')};
  border-radius: 0.375rem;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #111827;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
`

export const CodeArea = styled(TextArea)`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
  white-space: pre;
`

export const Tokens = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: ${MUTED};
`

export const Token = styled.button`
  padding: 0.125rem 0.5rem;
  border: 1px solid #bfdbfe;
  border-radius: 9999px;
  background-color: #eff6ff;
  color: #1d4ed8;
  font-size: 0.75rem;
  cursor: pointer;

  &:hover {
    background-color: #dbeafe;
  }
`

export const Problem = styled.div`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: ${({ $blocking }) => ($blocking ? ERROR_COLOR : WARNING_COLOR)};
`

// Looks like the text the patient gets, so the clinic reads a message, not a
// template.
export const Bubble = styled.div`
  align-self: flex-start;
  max-width: 28rem;
  margin-top: 1rem;
  padding: 0.625rem 0.875rem;
  border-radius: 1rem 1rem 1rem 0.25rem;
  background-color: #f3f4f6;
  color: #111827;
  font-size: 0.85rem;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

export const Count = styled.div`
  margin-top: 0.25rem;
  font-size: 0.7rem;
  color: ${MUTED};
`

export const FieldError = styled.div`
  margin-top: 0.75rem;
  font-size: 0.75rem;
  color: ${ERROR_COLOR};
`

export const PlaceholderList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.5rem;
`

export const Placeholder = styled.code`
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  background-color: #f3f4f6;
  color: #374151;
  font-size: 0.75rem;
`

export const ModeSwitch = styled.button`
  align-self: flex-start;
  margin-top: 1.25rem;
  padding: 0;
  border: none;
  background: none;
  color: #2563eb;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

export const Column = styled.div`
  display: flex;
  flex-direction: column;
`

// The token goes where the cursor is, replacing a selection, and the cursor
// lands after it, so the clinic keeps typing as if they had written it.
export const tokenInserter = (boxRef, text, setText) => label => {
  const box = boxRef.current
  const token = '[' + label + ']'
  const start = box ? box.selectionStart : text.length
  const end = box ? box.selectionEnd : text.length

  setText(text.slice(0, start) + token + text.slice(end))

  requestAnimationFrame(() => {
    if (!box) return
    box.focus()
    box.setSelectionRange(start + token.length, start + token.length)
  })
}

export const MessagePreview = ({ message }) =>
  message.trim() ? (
    <>
      <Bubble aria-label="Preview">{message}</Bubble>
      <Count>
        {message.length + ' characters · ' + smsCount(message) + ' SMS'}
      </Count>
    </>
  ) : null
